import { useFetcher, useLoaderData } from '@remix-run/react';
import { useEffect, useState } from 'react';
import { loader } from '~/routes/_index';
import type { Feed, FeedEntry } from '~/services/layout';
import { track } from '~/services/analytics/index.client';
import debounce from 'lodash.debounce';
import { type UseIsLoggedInReturnType } from './use-is-logged-in';

export const FAVORITE_DEBOUNCE_DELAY: number = 1000;

export type UseFavoriteActionReturnType = {
  isFavorite: boolean;
  favoriteAction: () => void;
  shouldRenderFavoriteAction: boolean;
  preventRenderByIsLoggedIn: boolean;
};

export function useFavoriteAction({
  entry,
  favoritesFeed,
  isLoggedInData,
}: {
  entry: FeedEntry;
  favoritesFeed: Feed | undefined;
  isLoggedInData: UseIsLoggedInReturnType | undefined;
}): UseFavoriteActionReturnType {
  const { isFavoritesSyncedFeed } = useLoaderData<typeof loader>();

  const fetcher: any = useFetcher({
    key: 'favorites',
  });

  const [isFavorite, setIsFavorite] = useState<boolean | null>(null);

  const isLoggedIn = isLoggedInData?.isLoggedIn;
  const preventRenderByIsLoggedIn = !!isLoggedInData?.preventRenderByIsLoggedIn;

  const entryId: string = entry?.id;

  useEffect(() => {
    if (fetcher.state === 'loading') {
      return;
    }

    if (!isLoggedIn) {
      setIsFavorite(false);
      return;
    }

    if (favoritesFeed && fetcher.state === 'idle') {
      const isFav = favoritesFeed?.entry?.some(
        (fav: FeedEntry) => fav.id === entryId
      );
      setIsFavorite(!!isFav);
    }
  }, [isLoggedInData, favoritesFeed, entryId, fetcher.state]);

  const debouncedFavoriteAction = debounce(
    (newIsFavorite: boolean) => {
      const favAction = newIsFavorite ? 'added' : 'removed';

      const stringifyEntry: string = getStringifyEntry(entry);

      fetcher.submit(
        { favAction, stringifyEntry },
        {
          method: 'post',
          action: `/api/favorite-action`,
        }
      );

      track('favclicked789', { entryId, isFavorite: newIsFavorite });
    },
    FAVORITE_DEBOUNCE_DELAY,
    {
      leading: true,
    }
  );

  const favoriteAction = () => {
    const newIsFavorite = !isFavorite;

    setIsFavorite(newIsFavorite);
    debouncedFavoriteAction(newIsFavorite);
  };

  const shouldRenderFavoriteAction = [
    !preventRenderByIsLoggedIn,
    isLoggedIn,
    entryId,
    isFavorite !== null,
    isFavoritesSyncedFeed,
  ].every(Boolean);

  return {
    isFavorite: !!isFavorite,
    favoriteAction,
    shouldRenderFavoriteAction,
    preventRenderByIsLoggedIn,
  };
}

/**
 * Get the stringified entry object with error handling
 * @param entry - The entry object
 * @returns - The stringified entry object or empty string
 */
function getStringifyEntry(entry: any): string {
  try {
    return JSON.stringify(entry);
  } catch (error) {
    return '';
  }
}
